import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Upload, Users, BarChart3, Home, Shield, Clock, Sparkles } from 'lucide-react';

const Layout = ({ children }) => {
  const location = useLocation();
  const [mexicoTime, setMexicoTime] = useState('');
  const [mexicoDate, setMexicoDate] = useState('');

  useEffect(() => {
    const updateMexicoTime = () => {
      const now = new Date();
      const options = {
        timeZone: 'America/Mexico_City',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      };
      const dateOptions = {
        timeZone: 'America/Mexico_City',
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      };
      setMexicoTime(now.toLocaleTimeString('es-MX', options));
      setMexicoDate(now.toLocaleDateString('es-MX', dateOptions));
    };

    updateMexicoTime();
    const interval = setInterval(updateMexicoTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const navItems = [
    { path: '/', icon: Home, label: 'Inicio' },
    { path: '/extract', icon: Upload, label: 'Extraer' },
    { path: '/profiles', icon: Users, label: 'Perfiles' },
    { path: '/compare', icon: BarChart3, label: 'Comparar' }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Game Background */}
      <div className="fixed inset-0 bg-gradient-to-b from-[#0a1628] via-[#1a3a5c] to-[#0a1628]">
        {/* Animated circles */}
        <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-400/5 rounded-full blur-3xl"></div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(rgba(77, 166, 255, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(77, 166, 255, 0.3) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
        
        {/* Floating particles */}
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-yellow-400 rounded-full animate-ping" style={{animationDuration: '3s'}}></div>
        <div className="absolute top-3/4 right-1/4 w-3 h-3 bg-cyan-400 rounded-full animate-ping" style={{animationDuration: '4s', animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-3/4 w-2 h-2 bg-orange-400 rounded-full animate-ping" style={{animationDuration: '5s', animationDelay: '2s'}}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-gradient-to-r from-[#0a1628]/95 via-[#1a3a5c]/90 to-[#0a1628]/95 backdrop-blur-md border-b-2 border-cyan-500/30">
        <div className="absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent"></div>
        
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-400 via-yellow-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/40 group-hover:shadow-orange-500/60 transition-all duration-300 border-2 border-yellow-300">
                  <Shield className="w-8 h-8 text-white drop-shadow-lg" />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-ping"></div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
              </div>
              <div>
                <h1 className="text-3xl game-title tracking-wider">LAST WAR</h1>
                <p className="text-xs game-subtitle tracking-[0.3em]">PROFILE EXTRACTOR</p>
              </div>
            </Link>

            {/* Mexico Time Display - Game Style */}
            <div className="hidden md:flex items-center gap-3 game-card px-5 py-2">
              <Clock className="w-6 h-6 text-cyan-400" />
              <div className="text-right">
                <p className="text-2xl font-mono font-bold text-white" style={{textShadow: '0 0 10px rgba(255,255,255,0.5)'}}>{mexicoTime}</p>
                <p className="text-xs text-cyan-300/80 capitalize">{mexicoDate}</p>
              </div>
              <div className="bg-gradient-to-r from-green-500 to-green-600 px-2 py-1 rounded-lg font-bold border border-green-400">
                <img src="/icons/mexico-flag.png" alt="México" className="w-8 h-5 object-cover rounded" />
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden lg:flex items-center gap-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-5 py-2 rounded-xl font-bold text-sm transition-all duration-300 ${
                      isActive
                        ? 'game-btn text-white'
                        : 'text-cyan-200 hover:text-white hover:bg-white/10 border-2 border-transparent hover:border-cyan-500/30'
                    }`}
                    style={isActive ? {} : {}}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </header>

      {/* Mobile Time Display */}
      <div className="md:hidden relative z-10 bg-[#0a1628]/90 border-b border-cyan-500/20">
        <div className="container mx-auto px-4 py-2 flex items-center justify-center gap-3">
          <Clock className="w-4 h-4 text-cyan-400" />
          <span className="text-lg font-mono font-bold text-white">{mexicoTime}</span>
          <span className="text-xs text-cyan-300/70 capitalize">{mexicoDate}</span>
          <img src="/icons/mexico-flag.png" alt="México" className="w-6 h-4 object-cover rounded" />
        </div>
      </div>

      {/* Main content */}
      <main className="relative z-10 container mx-auto px-4 py-8 pb-24 lg:pb-20">
        {children}
      </main>

      {/* Footer - TEAM MEXi */}
      <footer className="fixed left-0 right-0 z-30 lg:bottom-0" style={{ bottom: '72px' }}>
        {/* Desktop Footer */}
        <div className="hidden lg:block" style={{ position: 'fixed', bottom: 0, left: 0, right: 0 }}>
          <div className="relative bg-gradient-to-r from-[#0a1628]/98 via-[#0d2847]/98 to-[#0a1628]/98 backdrop-blur-md border-t-2 border-orange-500/50">
            {/* Glowing line */}
            <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-orange-400 to-transparent"></div>
            <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-yellow-300 to-transparent animate-pulse"></div>
            
            <div className="container mx-auto px-4 py-3">
              <div className="flex items-center justify-center gap-4">
                {/* Left decorative element */}
                <div className="hidden md:flex items-center gap-2">
                  <div className="w-8 h-[2px] bg-gradient-to-r from-transparent to-orange-400"></div>
                  <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                  <div className="w-12 h-[2px] bg-gradient-to-r from-orange-400 to-yellow-400"></div>
                  <Sparkles className="w-5 h-5 text-yellow-400" />
                </div>
                
                {/* Main text */}
                <div className="flex items-center gap-3">
                  <span className="text-cyan-400 font-bold text-sm tracking-wider">SISTEMA DESARROLLADO POR</span>
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-yellow-500 blur-lg opacity-50 animate-pulse"></div>
                    <div className="relative px-4 py-1.5 bg-gradient-to-r from-orange-500 via-yellow-500 to-orange-500 rounded-lg border-2 border-yellow-300 shadow-lg shadow-orange-500/50">
                      <span className="text-xl font-black text-white tracking-wider" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.5)'}}>
                        TEAM: MEXi
                      </span>
                    </div>
                  </div>
                  <img src="/icons/mexico-flag.png" alt="México" className="w-12 h-8 object-cover rounded shadow-lg" />
                </div>
                
                {/* Right decorative element */}
                <div className="hidden md:flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-yellow-400" />
                  <div className="w-12 h-[2px] bg-gradient-to-l from-orange-400 to-yellow-400"></div>
                  <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                  <div className="w-8 h-[2px] bg-gradient-to-l from-transparent to-orange-400"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Footer - Above bottom nav */}
        <div className="lg:hidden">
          <div className="relative bg-gradient-to-r from-[#0a1628]/98 via-[#0d2847]/98 to-[#0a1628]/98 backdrop-blur-md border-t border-orange-500/30">
            <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-orange-400 to-transparent animate-pulse"></div>
            <div className="py-2.5 px-4">
              <div className="flex flex-col items-center justify-center gap-1">
                <span className="text-cyan-400 font-bold text-xs tracking-wider">DESARROLLADO POR</span>
                <div className="px-4 py-1.5 bg-gradient-to-r from-orange-500 via-yellow-500 to-orange-500 rounded-lg border border-yellow-300 shadow-lg shadow-orange-500/30 flex items-center justify-center gap-2">
                  <span className="text-base font-black text-white" style={{textShadow: '1px 1px 2px rgba(0,0,0,0.5)'}}>TEAM: MEXi</span>
                  <img src="/icons/mexico-flag.png" alt="México" className="w-8 h-5 object-cover rounded shadow" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Bottom navigation for mobile */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-[#0a1628] via-[#1a3a5c] to-[#0a1628] border-t-2 border-cyan-500/30">
        <div className="flex justify-around py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all duration-300 ${
                  isActive
                    ? 'text-orange-400'
                    : 'text-cyan-300 hover:text-white'
                }`}
              >
                <div className={`p-2 rounded-xl ${isActive ? 'bg-gradient-to-r from-orange-500/30 to-yellow-500/30 border border-orange-500/50' : ''}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className="text-xs font-bold">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Footer spacer for mobile (nav + footer) */}
      <div className="lg:hidden h-36"></div>
      {/* Footer spacer for desktop */}
      <div className="hidden lg:block h-16"></div>
    </div>
  );
};

export default Layout;
